<template>
  <button
    type="button"
    class="s-imagePreview"
    :class="{ '-hasFinalImage': !isLoading }"
    :style="aspectRatioStyles"
    @click="handleClick">
    <span
      class="s-imagePreview__spacer"
      :style="aspectRatioStyles" />

    <img
      v-if="previewBase64 && showPreview"
      class="s-imagePreview__preview"
      :src="previewBase64"
      alt=""
      aria-hidden="true"
      draggable="false" />

    <img
      v-if="src"
      class="s-imagePreview__image"
      :class="{ '-visible': isLoaded && showPreview, '-visibleInstant': isLoaded && !showPreview }"
      :src="src"
      draggable="false"
      @load="onLoad" />
    <span
      v-if="isLoading"
      class="s-imagePreview__loading"
      :class="{ '-canCancel': canCancel }">
      <LoadingSpinner v-if="!uploadedPercentage" />
      <template v-else>
        <PercentageBasedLoadingIndicator
          :percentage="uploadedPercentage"
          :large="canCancel" />
        <button
          v-if="canCancel"
          type="button"
          class="s-imagePreview__cancelButton"
          @click.stop="emit('cancel')">
          <SvgIcon
            type="close"
            small />
        </button>
      </template>
    </span>
  </button>
</template>

<script setup>
import {
  computed,
  ref,
  onBeforeUnmount,
} from 'vue'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PercentageBasedLoadingIndicator from '@/components/PercentageBasedLoadingIndicator.vue'
import SvgIcon from '@/components/SvgIcon.vue'

const props = defineProps({
  src: {
    type: String,
    default: null,
  },
  width: {
    type: Number,
    default: null,
  },
  height: {
    type: Number,
    default: null,
  },
  previewBase64: {
    type: String,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  uploadedPercentage: {
    type: Number,
    default: null,
  },
  canCancel: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click', 'cancel'])

const isLoaded = ref(false)

const aspectRatioStyles = computed(() => {
  const styles = {
    aspectRatio: '16 / 9',
    // height did not work on Safari iPhone with aspect ratio, So the image was not displayed properly
    // so we used minHeight & maxHeight to keep it at same aspect ratio
    minHeight: '200px',
    maxHeight: '200px',
  }
  if (props.width && props.height) {
    styles.aspectRatio = `${props.width} / ${props.height}`
    if (props.height < 200) {
      styles.minHeight = `${props.height}px`
      styles.maxHeight = `${props.height}px`
    }
  }
  return styles
})

// Only show the blurred preview if the image takes longer than 200ms to load
const showPreview = ref(false)
let previewTimerId = null

previewTimerId = setTimeout(() => {
  showPreview.value = true
  previewTimerId = null
}, 200)

function clearPreviewTimer() {
  if (previewTimerId) {
    clearTimeout(previewTimerId)
    previewTimerId = null
  }
}

onBeforeUnmount(() => {
  clearPreviewTimer()
})

function onLoad() {
  isLoaded.value = true
  clearPreviewTimer()
}

function handleClick() {
  if (isLoaded.value) {
    emit('click')
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

.s-imagePreview {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  border-radius: $spacing-2;
  cursor: default;

  &.-hasFinalImage {
    cursor: zoom-in;
  }
}

.s-imagePreview__spacer {
  width: 100%;
}

// Base layer: tiny blurred preview
.s-imagePreview__preview {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(18px);
}

// Real image faded in on top
.s-imagePreview__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0;
  filter: blur(18px);
  transition: all 250ms ease;
  /* This is important when uploading svgs, so the background isn't transparent */
  background-color: white;
  background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.s-imagePreview__image.-visible {
  opacity: 1;
  filter: blur(0px);
}

.s-imagePreview__image.-visibleInstant {
  opacity: 1;
  filter: blur(0px);
  transition: none;
}

.s-imagePreview__loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;

  &.-canCancel {
    pointer-events: auto;
  }
}

.s-imagePreview__cancelButton {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: $color-white;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
}
</style>
